import { useCallback, useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { useSettingsNavigate } from '@frontend/settings-routing';

export type BookingSiteSetupReasonCode =
  | 'OS_CUSTOMIZATION_OFF'
  | 'NO_BOOKABLE_PROVIDER'
  | 'NO_BOOKABLE_APPOINTMENT_TYPE'
  | 'NO_PROVIDER_APPOINTMENT_MAPPING'
  | 'NO_SCHEDULE_DEFINITION_AVAILABILITY';

export type BookingSiteSetupMessage = {
  primaryMessage: string;
  secondaryMessage?: string;
  tertiaryMessage?: string;
};

type UseBookingSiteSetupReasonActionArgs = {
  reasonCode?: BookingSiteSetupReasonCode | null;
  locationId?: string;
  isMultiLocation?: boolean;
  isScheduleDefinitionsEnabled?: boolean;
};

export function getBookingSiteSetupMessage(
  reasonCode: BookingSiteSetupReasonCode | null,
  options: {
    t: (key: string, vars?: Record<string, string>) => string;
    appointmentEntityLabel: string;
    isMultiLocation: boolean;
    isScheduleDefinitionsEnabled: boolean;
  }
): BookingSiteSetupMessage {
  const { t, appointmentEntityLabel, isMultiLocation, isScheduleDefinitionsEnabled } = options;

  switch (reasonCode) {
    case 'OS_CUSTOMIZATION_OFF':
      return {
        primaryMessage: t("Online booking isn't enabled yet for this location. Ask your admin to turn it on."),
      };

    case 'NO_BOOKABLE_PROVIDER':
      return {
        primaryMessage: t('Add provider to get started.'),
        secondaryMessage: t("You'll also need at least one {{entityLabel}} to be set up.", {
          entityLabel: appointmentEntityLabel.toLowerCase(),
        }),
        tertiaryMessage: isScheduleDefinitionsEnabled
          ? t('Then set your availability so patients can book at the right time.')
          : '',
      };

    case 'NO_BOOKABLE_APPOINTMENT_TYPE':
      return {
        primaryMessage: t("Add {{entityLabel}} so customers know what they're booking.", {
          entityLabel: appointmentEntityLabel.toLowerCase(),
        }),
        secondaryMessage: t("You'll also need at least one provider to be set up."),
        tertiaryMessage: isScheduleDefinitionsEnabled
          ? t('Then set your availability so patients can book at the right time.')
          : '',
      };

    case 'NO_SCHEDULE_DEFINITION_AVAILABILITY':
      return {
        primaryMessage: isMultiLocation
          ? t('Set your availability for this location so patients know when they can book.')
          : t('Set your availability so patients know when they can book.'),
      };

    case 'NO_PROVIDER_APPOINTMENT_MAPPING':
    default:
      return {
        primaryMessage: isMultiLocation
          ? t(
              "You're almost there! Before you can add the booking link to your website for this location, you'll first need to connect your providers to {{entityLabel}}s.",
              { entityLabel: appointmentEntityLabel.toLowerCase() }
            )
          : t(
              "You're almost there! Before you can add the booking link to your website, you'll first need to connect your providers to {{entityLabel}}s.",
              { entityLabel: appointmentEntityLabel.toLowerCase() }
            ),
      };
  }
}

export const useBookingSiteSetupReasonAction = ({
  reasonCode = null,
  locationId,
  isMultiLocation = false,
  isScheduleDefinitionsEnabled = false,
}: UseBookingSiteSetupReasonActionArgs = {}) => {
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const { t } = useTranslation('schedule');

  const appointmentEntityLabel = isScheduleDefinitionsEnabled ? t('Service') : t('Appointment Type');

  const shouldShowAction = reasonCode !== 'OS_CUSTOMIZATION_OFF';

  const ctaLabel = useMemo(() => {
    if (!shouldShowAction) return;
    switch (reasonCode) {
      case 'NO_BOOKABLE_PROVIDER':
        return t('Create Provider');
      case 'NO_BOOKABLE_APPOINTMENT_TYPE':
        return t('Create {{entityLabel}}', { entityLabel: appointmentEntityLabel });
      case 'NO_SCHEDULE_DEFINITION_AVAILABILITY':
        return t('Create Availability');
      case 'NO_PROVIDER_APPOINTMENT_MAPPING':
      default:
        return t('Start Mapping');
    }
  }, [shouldShowAction, reasonCode, t, appointmentEntityLabel]);

  const message = useMemo(
    () =>
      getBookingSiteSetupMessage(reasonCode, {
        t,
        appointmentEntityLabel,
        isMultiLocation,
        isScheduleDefinitionsEnabled,
      }),
    [reasonCode, t, appointmentEntityLabel, isMultiLocation, isScheduleDefinitionsEnabled]
  );

  const onCtaClick = useCallback(() => {
    if (!shouldShowAction) return;

    switch (reasonCode) {
      case 'NO_BOOKABLE_PROVIDER':
        if (locationId && isMultiLocation) {
          settingsNavigate({ to: '/schedule/manage-providers/:locationId', params: { locationId } });
          return;
        }
        settingsNavigate({ to: '/schedule/manage-providers' });
        return;
      case 'NO_SCHEDULE_DEFINITION_AVAILABILITY':
        if (locationId && isMultiLocation) {
          settingsNavigate({ to: '/schedule/availability/:locationId', params: { locationId } });
          return;
        }
        settingsNavigate({ to: '/schedule/availability' });
        return;
      case 'NO_BOOKABLE_APPOINTMENT_TYPE':
      case 'NO_PROVIDER_APPOINTMENT_MAPPING':
      default:
        if (locationId && isMultiLocation) {
          settingsNavigate({
            to: isScheduleDefinitionsEnabled
              ? '/schedule/services/:locationId'
              : '/schedule/appointment-types/:locationId',
            params: { locationId },
          });
          return;
        }

        settingsNavigate({ to: isScheduleDefinitionsEnabled ? '/schedule/services' : '/schedule/appointment-types' });
        return;
    }
  }, [shouldShowAction, reasonCode, locationId, isMultiLocation, settingsNavigate, isScheduleDefinitionsEnabled]);

  return {
    message,
    shouldShowAction,
    ctaLabel,
    onCtaClick,
  };
};
